/**
 * The ONE place this app reads `import.meta.env`.
 *
 * MAR-150: `VITE_API_BASE_URL` used to be read in two files with two different behaviours —
 * `services/api.ts` fell back to the *dev* API URL (so a production bundle would have
 * silently called dev), while `DiscountGovernanceSection.tsx` had no fallback at all and
 * produced `fetch("undefined/workspaces/…")`. Same variable, same bundle, two answers. A
 * single read site is what stops that recurring; an ESLint rule in `.eslintrc.cjs` makes
 * `import.meta.env` an error everywhere else, and a drift test pins that this file is the
 * only match.
 *
 * There are no fallbacks for required values. A wrong-but-present default is what let a
 * broken bundle ship green.
 */

/**
 * In a deployed bundle this throw is unreachable by construction: the build refuses before
 * the bundle exists (`config/vite-plugin-require-env.mjs`). It is here for the two paths the
 * build guard deliberately does not cover — `vite dev` and `vite preview`. The page is still
 * blank there; what changes is that the console names the variable, instead of reporting an
 * invalid key from inside ClerkProvider. Same shape as
 * `packages/shared/src/middleware/auth.js:107`.
 */
function required(name: string, value: string | undefined): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    throw new Error(
      `${name} is not set. Add it to packages/web-app/.env (see .env.example). ` +
        'A production build refuses outright rather than substituting a placeholder.'
    );
  }
  return trimmed;
}

/** `${API_BASE_URL}/workspaces` must not become `//workspaces`. */
function withoutTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

// Literal member expressions on purpose — Vite replaces these statically at build time, and
// it cannot do that through a variable or a loop.
export const CLERK_PUBLISHABLE_KEY = required(
  'VITE_CLERK_PUBLISHABLE_KEY',
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
);

export const API_BASE_URL = withoutTrailingSlash(
  required('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL)
);

/**
 * Optional by design, not by omission: `App.tsx` guards on `isEmbedded() && SHOPIFY_API_KEY`,
 * so an absent value skips App Bridge init rather than initialising it with an empty key.
 */
export const SHOPIFY_API_KEY = (import.meta.env.VITE_SHOPIFY_API_KEY ?? '').trim();

/** Compared against `config/env-contract.mjs` by the drift test. Keep in step. */
export const REQUIRED_VITE_VARS = ['VITE_CLERK_PUBLISHABLE_KEY', 'VITE_API_BASE_URL'] as const;
